import React, { useEffect, useState } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import * as Tabs from "@radix-ui/react-tabs";
import { BookmarkPlus, Check, Info } from "lucide-react";
import Header from "../components/Header";
import WeekSelector from "../components/WeekSelector";
import BoxScoresTable from "../components/BoxScoresTable";
import WeeklyAwardsTable from "../components/WeeklyAwardsTable";
import PowerRankingsTable from "../components/PowerRankingsTable";
import LuckIndexTable from "../components/LuckIndexTable";
import StandingsTable from "../components/StandingsTable";
import NaughtyList from "../components/NaughtyList";
import { safeFetch, fetchWithRetry } from "../utils/api";
import { useAuth } from "../components/AuthContext";
import { useAttribution } from "../components/AttributionContext";
import { providerPersistsHistory } from "../utils/providerPolicy";
import { toast } from "sonner";

const WEEK_TABS = new Set(["awards", "standings", "boxscores", "power", "luck"]);

const TAB_LIST = [
    { value: "awards",    label: "Weekly Awards" },
    { value: "standings", label: "Standings" },
    { value: "boxscores", label: "Box Scores" },
    { value: "power",     label: "Power Rankings" },
    { value: "luck",      label: "Luck Index" },
];

const SharedLeaguePage = () => {
    const { code } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { isAuthenticated } = useAuth();

    // Share token info
    const [shareInfo, setShareInfo] = useState(null);
    const { setProviders: setAttributionProviders } = useAttribution();

    // Tell the global footer which provider's data this page shows (the share
    // URL carries no provider segment), so the Yahoo attribution can render.
    useEffect(() => {
        setAttributionProviders(shareInfo?.provider ? [shareInfo.provider] : []);
        return () => setAttributionProviders([]);
    }, [shareInfo?.provider, setAttributionProviders]);
    const [shareError, setShareError] = useState(null);

    // Save state
    const [isSaved, setIsSaved] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [ctaDismissed, setCtaDismissed] = useState(false);

    // League data (mirrors LeaguePage)
    const [currentWeek, setCurrentWeek] = useState(null);
    const [selectedWeek, setSelectedWeek] = useState(null);
    const [nCompletedWeeks, setNCompletedWeeks] = useState(null);
    const [completedWeeks, setCompletedWeeks] = useState([]);
    const [weekLabels, setWeekLabels] = useState({});
    const [tabData, setTabData] = useState({
        awards: null, standings: null, boxScores: null,
        powerRankings: null, luckIndex: null, naughtyList: null,
    });
    const [tabLoading, setTabLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("awards");
    const [fetchError, setFetchError] = useState(null);
    const [leagueNotStarted, setLeagueNotStarted] = useState(false);

    if (fetchError) throw fetchError;

    // Step 1: fetch share token info (fully public — plain fetch, no auth header needed)
    useEffect(() => {
        fetch(`/api/share/${code}/`)
            .then((r) => {
                if (!r.ok) {
                    return r.json().then((d) =>
                        Promise.reject(new Error(d.error || "Invalid share link."))
                    );
                }
                return r.json();
            })
            .then((data) => setShareInfo(data))
            .catch((err) => setShareError(err.message));
    }, [code]);

    // Step 2: once we have the league identity, fetch current week
    useEffect(() => {
        if (!shareInfo) return;
        const { provider, league_year, league_id } = shareInfo;

        const queryParams = new URLSearchParams(location.search);
        const weekFromUrl = (() => {
            const w = queryParams.get("week");
            return w && !isNaN(parseInt(w)) ? parseInt(w) : null;
        })();
        safeFetch(`/api/league/${provider}/${league_year}/${league_id}/current-week/`, {}, false, 2)
            .then((data) => {
                if (data?.redirect) {
                    navigate(data.redirect);
                } else {
                    const completedCount =
                        data.n_completed_weeks ??
                        Math.max((data.current_week ?? 1) - 1, 0);
                    const completedWeekOptions = data.completed_weeks?.length
                        ? data.completed_weeks.map(Number).sort((a, b) => a - b)
                        : Array.from(
                              { length: completedCount },
                              (_, i) => i + 1
                          );
                    if (completedWeekOptions.length < 1) {
                        setLeagueNotStarted(true);
                        return;
                    }
                    const requestedCompletedWeeks = weekFromUrl
                        ? completedWeekOptions
                              .filter((week) => week <= weekFromUrl)
                        : [];
                    const displayWeek = weekFromUrl
                        ? requestedCompletedWeeks[
                              requestedCompletedWeeks.length - 1
                          ] ?? completedWeekOptions[0]
                        : completedWeekOptions[completedWeekOptions.length - 1];

                    setCurrentWeek(data.current_week);
                    setNCompletedWeeks(completedCount);
                    setCompletedWeeks(completedWeekOptions);
                    setWeekLabels(data.week_labels || {});
                    setSelectedWeek(displayWeek);
                    if (weekFromUrl !== displayWeek) {
                        const queryParams = new URLSearchParams(location.search);
                        queryParams.set("week", displayWeek);
                        window.history.replaceState(
                            null,
                            "",
                            `${location.pathname}?${queryParams.toString()}`
                        );
                    }
                }
            })
            .catch((err) => setFetchError(err));
    }, [shareInfo]); // eslint-disable-line react-hooks/exhaustive-deps

    // Step 3: prefetch all tab data in parallel when selectedWeek changes
    useEffect(() => {
        if (!shareInfo || !selectedWeek) return;
        const { provider, league_year, league_id } = shareInfo;
        let cancelled = false;
        setTabLoading(true);

        Promise.all([
            safeFetch(`/api/weekly-awards/${provider}/${league_year}/${league_id}/${selectedWeek}/`, {}, false, 2),
            safeFetch(`/api/standings/${provider}/${league_year}/${league_id}/${selectedWeek}/`, {}, false, 2),
            safeFetch(`/api/box-scores/${provider}/${league_year}/${league_id}/${selectedWeek}/`, {}, false, 2),
            safeFetch(`/api/power-rankings/${provider}/${league_year}/${league_id}/${selectedWeek}/`, {}, false, 2),
            safeFetch(`/api/luck-index/${provider}/${league_year}/${league_id}/${selectedWeek}/`, {}, false, 2),
            safeFetch(`/api/naughty-list/${provider}/${league_year}/${league_id}/${selectedWeek}/`, {}, false, 2),
        ])
            .then(([awards, standings, boxScores, powerRankings, luckIndex, naughtyList]) => {
                if (cancelled) return;
                setTabData({ awards, standings, boxScores, powerRankings, luckIndex, naughtyList });
            })
            .catch((err) => { if (!cancelled) setFetchError(err); })
            .finally(() => { if (!cancelled) setTabLoading(false); });

        return () => { cancelled = true; };
    }, [shareInfo, selectedWeek, navigate]);

    const handleWeekChange = (newWeek) => {
        setSelectedWeek(newWeek);
        setTabLoading(true);
        const queryParams = new URLSearchParams(location.search);
        queryParams.set("week", newWeek);
        window.history.replaceState(null, "", `${location.pathname}?${queryParams.toString()}`);
    };

    const handleSave = async () => {
        setSaveLoading(true);
        try {
            const response = await fetchWithRetry(
                `/api/share/${code}/save/`,
                { method: "POST" },
                1
            );
            const data = await response.json();
            if (response.ok) {
                setIsSaved(true);
                toast.success(`"${shareInfo.league_name}" saved to your dashboard`);
            } else {
                toast.error(data.error || "Failed to save league.");
            }
        } catch {
            toast.error("An unexpected error occurred.");
        } finally {
            setSaveLoading(false);
        }
    };

    // Invalid / revoked token
    if (shareError) {
        return (
            <div className="min-h-screen flex flex-col bg-bg">
                <Header />
                <div className="flex-1 flex items-center justify-center px-6">
                    <div className="text-center max-w-sm">
                        <p className="font-display text-2xl font-bold text-ds-text mb-2">
                            Link not found
                        </p>
                        <p className="text-text-muted text-sm font-semibold leading-relaxed">
                            {shareError}
                        </p>
                    </div>
                </div>
            </div>
        );
    }

    // Loading
    if (!shareInfo || currentWeek === null) {
        return (
            <div className="min-h-screen bg-bg flex flex-col">
                <Header />
                <div className="flex-1 flex items-center justify-center">
                    <div className="text-center">
                        <div className="w-12 h-12 border-4 border-forest border-t-mustard rounded-full animate-spin mx-auto mb-4" />
                        <p className="text-text-muted font-sans font-semibold">Loading league data…</p>
                    </div>
                </div>
            </div>
        );
    }

    const { league_name, league_year, league_id, provider } = shareInfo;
    const simulationUrl = `/fantasy_stats/simulation/${provider}/${league_year}/${league_id}`;
    const historyUrl = `/fantasy_stats/league-history/${provider}/${league_year}/${league_id}/`;
    const showHistoryLink = providerPersistsHistory(provider);

    if (leagueNotStarted) {
        return (
            <div className="min-h-screen bg-bg flex flex-col">
                <Header />
                <div className="bg-forest border-b-4 border-mustard">
                    <div className="max-w-5xl mx-auto px-6 py-6">
                        <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                            <div>
                                <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                                    {league_year} Season
                                </p>
                                <h1 className="font-display text-4xl font-black text-white leading-none">
                                    {league_name}
                                </h1>
                                <p className="text-white/50 font-sans text-sm font-semibold mt-2">
                                    League ID: {league_id}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
                <main className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-10">
                    <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 border-l-4 border-l-amber-400 rounded-lg px-4 py-3 mb-8 max-w-2xl">
                        <Info className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                        <div>
                            <p className="text-amber-800 font-semibold text-sm">Season hasn't started yet</p>
                            <p className="text-amber-700 text-xs mt-0.5">
                                Week 1 hasn't begun. Stats, awards, and power rankings will appear once the season kicks off.
                            </p>
                        </div>
                    </div>
                    <p className="text-xs font-extrabold uppercase tracking-widest text-text-muted mb-3">
                        While you wait…
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-xl">
                        {showHistoryLink && (
                        <Link
                            to={historyUrl}
                            className="bg-surface border border-border rounded-xl p-5 hover:border-forest/40 hover:shadow-sm transition-all group"
                        >
                            <div className="text-2xl mb-2">📊</div>
                            <div className="font-bold text-forest text-sm group-hover:underline">League History</div>
                            <div className="text-text-muted text-xs mt-1">Browse records and stats from previous seasons.</div>
                        </Link>
                        )}
                        <Link
                            to="/dashboard"
                            className="bg-surface border border-border rounded-xl p-5 hover:border-forest/40 hover:shadow-sm transition-all group"
                        >
                            <div className="text-2xl mb-2">🏠</div>
                            <div className="font-bold text-forest text-sm group-hover:underline">Dashboard</div>
                            <div className="text-text-muted text-xs mt-1">Return to your leagues overview.</div>
                        </Link>
                    </div>
                </main>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-bg flex flex-col">
            <Header />

            {/* Soft CTA for unauthenticated visitors */}
            {!isAuthenticated && !ctaDismissed && (
                <div className="bg-mustard/10 border-b border-mustard/30">
                    <div className="max-w-5xl mx-auto px-6 py-3 flex items-center justify-between gap-4">
                        <p className="text-sm font-semibold text-ds-text">
                            Want easy access to this league?{" "}
                            <Link
                                to="/register"
                                className="text-green font-bold hover:text-mustard transition-colors"
                            >
                                Create a free Doritostats account →
                            </Link>
                        </p>
                        <button
                            onClick={() => setCtaDismissed(true)}
                            className="text-text-muted hover:text-ds-text text-xs font-bold shrink-0 transition-colors"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            {/* Hero Band */}
            <div className="bg-forest border-b-4 border-mustard">
                <div className="max-w-5xl mx-auto px-6 py-6">
                    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
                        <div>
                            <p className="text-mustard text-xs font-sans font-bold uppercase tracking-widest mb-2">
                                {league_year} Season
                            </p>
                            <h1 className="font-display text-4xl font-black text-white leading-none">
                                {league_name}
                            </h1>
                            <p className="text-white/50 font-sans text-sm font-semibold mt-2">
                                League ID: {league_id}
                            </p>
                        </div>
                        <div className="flex gap-2 items-center flex-shrink-0 flex-wrap">
                            {isAuthenticated && !isSaved && (
                                <button
                                    onClick={handleSave}
                                    disabled={saveLoading}
                                    className="flex items-center gap-1.5 px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide bg-mustard text-forest rounded shadow-mustard hover:bg-yellow-500 transition-colors disabled:opacity-60"
                                >
                                    <BookmarkPlus className="w-3.5 h-3.5" />
                                    {saveLoading ? "Saving…" : "Save to My Leagues"}
                                </button>
                            )}
                            {isAuthenticated && isSaved && (
                                <span className="flex items-center gap-1.5 px-4 py-2 text-xs font-bold text-white/70 border border-white/20 rounded">
                                    <Check className="w-3.5 h-3.5" />
                                    Saved
                                </span>
                            )}
                            <Link
                                to={simulationUrl}
                                className="px-4 py-2 text-xs font-sans font-bold uppercase tracking-wide text-white/75 border border-white/20 rounded hover:text-white hover:border-white/40 transition-colors"
                            >
                                Simulate →
                            </Link>
                        </div>
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="flex-1 max-w-5xl mx-auto w-full px-4 sm:px-6 py-8">
                <Tabs.Root defaultValue="awards" onValueChange={setActiveTab}>
                    <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between mb-6">
                        <div className="relative">
                            <Tabs.List className="flex gap-1 bg-surface border border-border rounded-lg p-1 overflow-x-auto scroll-smooth">
                                {TAB_LIST.map(({ value, label }) => (
                                    <Tabs.Trigger
                                        key={value}
                                        value={value}
                                        className="
                                            px-3 py-2 text-xs font-sans font-bold uppercase tracking-wide
                                            rounded whitespace-nowrap transition-colors duration-150
                                            text-text-muted hover:text-forest
                                            data-[state=active]:bg-forest data-[state=active]:text-white
                                        "
                                    >
                                        {label}
                                    </Tabs.Trigger>
                                ))}
                            </Tabs.List>
                            <div className="pointer-events-none absolute inset-y-0 right-0 w-8 rounded-r-lg bg-gradient-to-l from-surface to-transparent sm:hidden" />
                        </div>

                        {WEEK_TABS.has(activeTab) && (
                            <WeekSelector
                                selectedWeek={selectedWeek}
                                minWeek={1}
                                maxWeek={currentWeek}
                                weeks={completedWeeks}
                                weekLabels={weekLabels}
                                onWeekChange={handleWeekChange}
                            />
                        )}
                    </div>

                    <Tabs.Content value="awards" className="outline-none">
                        <div className="flex items-baseline gap-3 border-b-2 border-ds-text pb-2 mb-4">
                            <h2 className="font-display text-2xl font-bold text-ds-text">
                                Weekly Awards
                            </h2>
                            <span className="font-sans text-sm text-text-muted font-semibold">
                                Week {selectedWeek}
                            </span>
                        </div>
                        <WeeklyAwardsTable
                            data={tabData.awards}
                            week={selectedWeek}
                            loading={tabLoading}
                            nCompletedWeeks={nCompletedWeeks}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="standings" className="outline-none">
                        <StandingsTable
                            data={tabData.standings}
                            week={selectedWeek}
                            loading={tabLoading}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="boxscores" className="outline-none">
                        <BoxScoresTable
                            data={tabData.boxScores}
                            week={selectedWeek}
                            loading={tabLoading}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="power" className="outline-none">
                        <PowerRankingsTable
                            data={tabData.powerRankings}
                            week={selectedWeek}
                            loading={tabLoading}
                            nCompletedWeeks={nCompletedWeeks}
                        />
                    </Tabs.Content>

                    <Tabs.Content value="luck" className="outline-none space-y-6">
                        <LuckIndexTable
                            data={tabData.luckIndex}
                            week={selectedWeek}
                            loading={tabLoading}
                            nCompletedWeeks={nCompletedWeeks}
                        />
                        <NaughtyList
                            data={tabData.naughtyList}
                            week={selectedWeek}
                            loading={tabLoading}
                            nCompletedWeeks={nCompletedWeeks}
                        />
                    </Tabs.Content>
                </Tabs.Root>
            </div>
        </div>
    );
};

export default SharedLeaguePage;
